import React from "react";
import Layout from "../../../../components/Layout";
import Container from "../../../../components/Container";
import Flex from "../../../../components/Flex";
import { Link, useHistory, useParams } from "react-router-dom";
import Content from "../../../../components/Content";
import Button from "../../../../components/Button";
import DataTable from "../../../../components/DataTable";
import ButtonList from "../../../../components/ButtonList";
import LinkButton from "../../../../components/LinkButton";
import Tooltip from "../../../../components/Tooltip";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../components/MessageProvider";
import Modal from "../../../../components/ModalPopup";
import { useAuthState } from "../../../../components/AuthProvider";
import Spinner from "../../../../components/Loader/Spinner";

import styles from "./ViewOrganization.module.scss";

const ViewOrganization = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  console.log("ENV: " + process.env.REACT_APP_ENV);
  const orgApiUrl = `${clientConfigApiDomain}/organizations`;

  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  // Replace useFetch with manual state management
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const clickableCell = ({
    cell: {
      value,
      row: { original: selectedClient }
    }
  }) => {
    const onClick = () => {
      history.push(`/admin/organizations/${id}/accounts/${selectedClient.id}`);
    };
    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };

  const onDeleteOrganization = async () => {
    try {
      setDeleteLoading(true);
      // This will go through your interceptor and get authz_token added
      const response = await fetch(`${orgApiUrl}/${id}/`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete organization: ${response.statusText}`);
      }

      toast.success(`Organization deleted successfully`);
      history.push("/admin/organizations");
    } catch (error) {
      console.error("Error deleting organization:", error);
      toast.error(`Organization deletion failed`);
    } finally {
      setDeleteLoading(false);
      setShowDialog(false);
    }
  };

  function NewLineText({ text }) {
    return text.split("\n").map((str, index) => (
      <React.Fragment key={index}>
        {str}
        <br />
      </React.Fragment>
    ));
  }

  React.useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchOrganization = async () => {
      try {
        setIsLoading(true);
        // This will go through your interceptor and get authz_token added automatically
        const response = await fetch(`${orgApiUrl}/${id}/`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch organization: ${response.statusText}`);
        }
        
        const organizationData = await response.json();
        setData(organizationData);
        setHasLoaded(true);
      } catch (error) {
        console.error("Error fetching organization:", error);
        toast.error("Failed to load organization details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganization();
  }, [id, orgApiUrl, toast]);

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedClient }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(`/admin/organizations/${id}/accounts/${selectedClient.id}`);
    };

    const onEditClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${selectedClient.id}/edit`
      );
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        {isEditVisible(id) && (
          <LinkButton icon="edit" onClick={onEditClick}>
            Edit
          </LinkButton>
        )}
      </ButtonList>
    );
  };

  const columnsDataTable = [
    {
      Header: "Account Name",
      accessor: "name",
      Cell: clickableCell
    },
    {
      Header: "Account OID",
      accessor: "oid"
    },
    {
      Header: "Short Code",
      accessor: "short_code"
    },
    {
      Header: "Active Lives",
      accessor: "active_lives"
    },
    {
      Header: "Active Records",
      accessor: "active_records"
    },
    {
      Header: "Maintenance Mode",
      accessor: row => (row.maintenance_mode ? "Enabled" : "Disabled")
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  const accountsDownloadColumns = [
    {
      Header: "Account Name",
      accessor: "name",
      Cell: clickableCell
    },
    {
      Header: "Account OID",
      accessor: "oid"
    },
    {
      Header: "Short Code",
      accessor: "short_code"
    },
    {
      Header: "Active Lives",
      accessor: "active_lives"
    },
    {
      Header: "Active Records",
      accessor: "active_records"
    }
  ];

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = "Loading ...";
  } else if (hasLoaded && data) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item last render={() => `Organization: ${data.name}`} />
      </Breadcrumbs>
    );
  }

  const isEditVisible = orgId => {
    return (
      user.role === "Global Admin" || user.orgIds.includes(parseInt(orgId))
    );
  };

  const getSecureGroups = () => {
    if (!data || data.short_code == null) {
      return "";
    }
    let suffix = null;
    if (process.env.REACT_APP_ENV == "stage") {
      suffix = "TEST";
    } else {
      suffix = process.env.REACT_APP_ENV.toUpperCase();
    }

    let secureGroups =
      "EIMP_UI_" +
      data.short_code +
      "_ORGADMIN_" +
      suffix +
      "\n" +
      "EIMP_UI_" +
      data.short_code +
      "_ALL_DS_" +
      suffix +
      "\n" +
      "EIMP_UI_" +
      data.short_code +
      "_ALL_READONLY_" +
      suffix +
      "\n" +
      "EIMP_UI_" +
      data.short_code +
      "_<CLIENT_SHORT_CODE>_DS_" +
      suffix +
      "\n" +
      "EIMP_UI_" +
      data.short_code +
      "_<CLIENT_SHORT_CODE>_READONLY_" +
      suffix +
      "\n";

    return secureGroups;
  };

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded && data ? (
            <Flex.Item span={12}>
              <Content>
                <h1>Organization: {data.name}</h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.orgSettings}>
                  <h3>Organization Details</h3>
                  <div>
                    {isEditVisible(id) && (
                      <Button
                        className={styles.margin}
                        primary
                        onClick={() => {
                          history.push(`/admin/organizations/${id}/edit`);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
                <div className={styles.orgDetails}>
                  <div>
                    <h4>Organization ID</h4>
                    <span>{data.id}</span>
                  </div>
                  <div>
                    <h4>Name</h4>
                    <span>{data.name}</span>
                  </div>
                  <div>
                    <h4>Short Code</h4>
                    <span>{data.short_code}</span>
                  </div>
                  <div>
                    <h4>
                      Use MRN as Source Entity ID
                      <Tooltip>
                        <Content>
                          <p>
                            When enabled, HCM will use MRN from the source as
                            Source Entity ID.
                          </p>
                        </Content>
                      </Tooltip>
                    </h4>
                    {data.use_mrn_as_source_entity_id ? (
                      <span>Enabled</span>
                    ) : (
                      <span>Disabled</span>
                    )}
                  </div>
                  {user.role === "Global Admin" && (
                    <div>
                      <h4>Maintenance Mode</h4>
                      {data.maintenance_mode ? (
                        <span>Enabled</span>
                      ) : (
                        <span>Disabled</span>
                      )}
                    </div>
                  )}
                  <div>
                    <h4>Login Mode</h4>
                    <span>{data.login_type}</span>
                  </div>
                  <div>
                    <h4>Created By</h4>
                    <span> {data.created_by} </span>
                  </div>
                  <div>
                    <h4>Date Created</h4>
                    <span>
                      {moment(data.created_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  <div>
                    <h4>Updated By</h4>
                    <span>{data.updated_by}</span>
                  </div>
                  <div>
                    <h4>Date Updated</h4>
                    <span>
                      {moment(data.updated_at)
                        .tz(user.preferences.timeZone)
                        .format("L, LTS z")}
                    </span>
                  </div>
                  {window.localStorage.getItem("LOGIN_TYPE") === "MS-Email" && (
                    <div>
                      <h4>Secure Groups</h4>
                      <NewLineText text={getSecureGroups()} />
                    </div>
                  )}
                </div>

                <div className={styles.orgAccounts}>
                  <h3>Organization Accounts</h3>
                  <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                    <LinkButton
                      icon="add"
                      disabled={!isEditVisible(id)}
                      onClick={() => {
                        history.push(
                          `/admin/organizations/${id}/accounts/add`,
                          [data.name]
                        );
                      }}
                    >
                      Add Account
                    </LinkButton>
                  </ButtonList>
                  <div>
                    <DataTable
                      columns={columnsDataTable}
                      data={data.clients || []}
                      csvColumns={accountsDownloadColumns}
                      hasDownloadCsv={true}
                      hasSorting
                      hasPagination
                    />
                  </div>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
      <Modal open={showDialog} onClose={() => setShowDialog(false)}>
        <div style={{ marginBottom: "1rem" }}>
          Are you sure you want to delete this organization?
        </div>
        <ButtonList>
          <Button 
            primary 
            type="submit" 
            onClick={onDeleteOrganization}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Confirm"}
          </Button>
          <Button onClick={() => setShowDialog(false)}>Cancel</Button>
        </ButtonList>
      </Modal>
    </Layout>
  );
};

export default ViewOrganization;